<!--
  PlayerHandPenalty.vue

  Displays a single player's remaining hand cards as a visual card fan
  with their penalty amount below.

  Used in the round-end penalties phase to show what cards each player
  was caught holding when someone went out.
-->

<template>
  <div
    class="player-hand-penalty"
    :class="{ animating: isAnimating }"
    :style="{ '--animation-delay': `${animationDelay}ms` }"
  >
    <!-- Player Header -->
    <div class="player-header">
      <div class="player-avatar" :class="{ bot: isBot }">
        <span v-if="isBot" class="material-symbols-outlined">smart_toy</span>
        <span v-else class="player-initials">{{ playerInitials }}</span>
      </div>
      <span class="player-name">{{ displayName }}</span>
    </div>

    <!-- Two-Column Card Display -->
    <div class="card-piles-row">
      <!-- Hand Pile (Left) -->
      <div class="card-pile">
        <div class="pile-header hand-header">Hand</div>
        <div class="pile-content">
          <template v-if="handCards.length > 0">
            <div class="card-fan" :style="{ '--card-scale': handCardScale }">
              <div
                v-for="(card, index) in visibleHandCards"
                :key="`hand-${card.suit}-${card.rank}-${index}`"
                class="fan-card"
                :class="{ visible: handRevealedCount > index }"
                :style="getCardFanStyle(index, visibleHandCards.length)"
              >
                <Card :card="card" size="mini" :faceDown="handRevealedCount <= index" />
              </div>
              <div v-if="handOverflowCount > 0" class="overflow-indicator">+{{ handOverflowCount }}</div>
            </div>
          </template>
          <div v-else class="empty-indicator">
            <span class="material-symbols-outlined">check</span>
          </div>
        </div>
        <div class="pile-total" :class="{ visible: showPenalty, penalty: handPenalty > 0 }">
          {{ handPenalty > 0 ? `-${handPenalty}` : '0' }}
        </div>
      </div>

      <!-- Foot Pile (Right) -->
      <div class="card-pile">
        <div class="pile-header foot-header">Foot</div>
        <div class="pile-content">
          <template v-if="footCards.length > 0">
            <div class="card-fan" :style="{ '--card-scale': footCardScale }">
              <div
                v-for="(card, index) in visibleFootCards"
                :key="`foot-${card.suit}-${card.rank}-${index}`"
                class="fan-card"
                :class="{ visible: footRevealedCount > index }"
                :style="getCardFanStyle(index, visibleFootCards.length)"
              >
                <Card :card="card" size="mini" :faceDown="footRevealedCount <= index" />
              </div>
              <div v-if="footOverflowCount > 0" class="overflow-indicator">+{{ footOverflowCount }}</div>
            </div>
          </template>
          <div v-else class="empty-indicator">
            <span class="material-symbols-outlined">check</span>
          </div>
        </div>
        <div class="pile-total" :class="{ visible: showPenalty, penalty: footPenalty > 0 }">
          {{ footPenalty > 0 ? `-${footPenalty}` : '0' }}
        </div>
      </div>
    </div>

    <!-- Total Penalty -->
    <div class="total-row" :class="{ visible: showPenalty }">
      <span v-if="penaltyAmount > 0" class="total-penalty">Total: -{{ penaltyAmount }}</span>
      <span v-else class="total-clean">
        <span class="material-symbols-outlined">thumb_up</span> Clean!
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import Card from '../cards/Card.vue'
import { useCards } from '@/composables/useCards'

// ============================================================================
// Types
// ============================================================================

interface CardData {
  rank: string
  suit: string
}

interface Props {
  playerId: string
  playerName: string
  displayName?: string
  isBot: boolean
  hand: CardData[]
  foot: CardData[]
  penaltyAmount: number
  isAnimating?: boolean
  animationDelay?: number
}

// ============================================================================
// Props & Emits
// ============================================================================

const props = withDefaults(defineProps<Props>(), {
  displayName: '',
  isAnimating: false,
  animationDelay: 0,
})

const emit = defineEmits<{
  animationComplete: []
}>()

// ============================================================================
// Constants
// ============================================================================

const MAX_VISIBLE_CARDS_PER_PILE = 7

// ============================================================================
// State
// ============================================================================

const handRevealedCount = ref(0)
const footRevealedCount = ref(0)
const showPenalty = ref(false)

// ============================================================================
// Composables
// ============================================================================

const { getCardPoints } = useCards()

// ============================================================================
// Computed
// ============================================================================

/**
 * Display name with fallback
 */
const displayNameComputed = computed(() => {
  return props.displayName || props.playerName
})

/**
 * Player initials for avatar
 */
const playerInitials = computed(() => {
  const name = displayNameComputed.value
  const parts = name.split(' ')
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

/**
 * Hand cards (all cards including red 3s)
 */
const handCards = computed(() => {
  return props.hand || []
})

/**
 * Foot cards (all cards including red 3s)
 */
const footCards = computed(() => {
  return props.foot || []
})

/**
 * Total card count
 */
const totalCardCount = computed(() => {
  return handCards.value.length + footCards.value.length
})

/**
 * Visible hand cards (capped)
 */
const visibleHandCards = computed(() => {
  return handCards.value.slice(0, MAX_VISIBLE_CARDS_PER_PILE)
})

/**
 * Visible foot cards (capped)
 */
const visibleFootCards = computed(() => {
  return footCards.value.slice(0, MAX_VISIBLE_CARDS_PER_PILE)
})

/**
 * Hand overflow count
 */
const handOverflowCount = computed(() => {
  return Math.max(0, handCards.value.length - MAX_VISIBLE_CARDS_PER_PILE)
})

/**
 * Foot overflow count
 */
const footOverflowCount = computed(() => {
  return Math.max(0, footCards.value.length - MAX_VISIBLE_CARDS_PER_PILE)
})

/**
 * Calculate scale factor based on card count
 */
function getScaleForCount(count: number): number {
  if (count <= 3) return 1.1
  if (count <= 5) return 0.95
  if (count <= 7) return 0.85
  return 0.75
}

/**
 * Calculate fan angle based on card count
 */
function getFanAngle(count: number): number {
  if (count <= 1) return 0
  if (count <= 3) return 25
  if (count <= 5) return 35
  return 45
}

/**
 * Get style for a card in the fan
 */
function getCardFanStyle(index: number, total: number): Record<string, string> {
  if (total <= 1) {
    return { transform: 'rotate(0deg)' }
  }

  const fanAngle = getFanAngle(total)
  const halfAngle = fanAngle / 2
  const step = fanAngle / (total - 1)
  const rotation = -halfAngle + (step * index)

  return {
    transform: `rotate(${rotation}deg)`,
    transformOrigin: 'bottom center'
  }
}

const handCardScale = computed(() => getScaleForCount(visibleHandCards.value.length))
const footCardScale = computed(() => getScaleForCount(visibleFootCards.value.length))

/**
 * Check if a card is a red 3
 */
function isRed3(card: CardData): boolean {
  const rank = String(card.rank || '').toUpperCase()
  const suit = String(card.suit || '').toLowerCase()
  return rank === '3' && (suit === 'hearts' || suit === 'diamonds')
}

/**
 * Calculate penalty for a single card (red 3s = 500, others = card point value)
 */
function getCardPenalty(card: CardData): number {
  if (isRed3(card)) {
    return 500 // Red 3s are always -500 penalty
  }
  return Math.abs(getCardPoints(card))
}

/**
 * Calculate penalty for hand cards
 */
const handPenalty = computed(() => {
  let penalty = 0
  for (const card of handCards.value) {
    penalty += getCardPenalty(card)
  }
  return penalty
})

/**
 * Calculate penalty for foot cards
 */
const footPenalty = computed(() => {
  let penalty = 0
  for (const card of footCards.value) {
    penalty += getCardPenalty(card)
  }
  return penalty
})

// ============================================================================
// Methods
// ============================================================================

/**
 * Delay helper
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Animate the card fan reveal for both piles
 */
async function animateReveal(): Promise<void> {
  const handCount = visibleHandCards.value.length
  const footCount = visibleFootCards.value.length

  // Wait for initial animation delay
  await delay(props.animationDelay)

  // Reveal hand cards first
  for (let i = 0; i <= handCount; i++) {
    handRevealedCount.value = i
    await delay(60) // 60ms per card
  }

  // Brief pause between piles
  await delay(150)

  // Then reveal foot cards
  for (let i = 0; i <= footCount; i++) {
    footRevealedCount.value = i
    await delay(60) // 60ms per card
  }

  // Brief pause, then show penalties
  await delay(200)
  showPenalty.value = true

  // Wait for penalty animation
  await delay(400)

  emit('animationComplete')
}

// ============================================================================
// Lifecycle
// ============================================================================

onMounted(() => {
  if (!props.isAnimating) {
    // Show everything immediately
    handRevealedCount.value = visibleHandCards.value.length
    footRevealedCount.value = visibleFootCards.value.length
    showPenalty.value = true
  }
})

watch(() => props.isAnimating, (animating) => {
  if (animating) {
    handRevealedCount.value = 0
    footRevealedCount.value = 0
    showPenalty.value = false
    animateReveal()
  }
}, { immediate: true })

// ============================================================================
// Expose
// ============================================================================

defineExpose({
  animateReveal,
})
</script>

<style scoped>
.player-hand-penalty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Player Header */
.player-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.player-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.2));
  border: 2px solid rgba(59, 130, 246, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #93c5fd;
}

.player-avatar.bot {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(139, 92, 246, 0.2));
  border-color: rgba(168, 85, 247, 0.5);
}

.player-avatar.bot .material-symbols-outlined {
  font-size: 1rem;
  color: #c4b5fd;
}

.player-initials {
  font-size: 0.7rem;
  font-weight: 700;
}

.player-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

/* Two-Column Layout */
.card-piles-row {
  display: flex;
  gap: 1rem;
  justify-content: center;
  width: 100%;
}

.card-pile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  min-width: 140px;
  flex: 1;
}

/* Pile Headers */
.pile-header {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  margin-top: -5px;
}

.hand-header {
  color: #60a5fa;
  background: rgba(59, 130, 246, 0.15);
}

.foot-header {
  color: #c084fc;
  background: rgba(168, 85, 247, 0.15);
}

/* Pile Content */
.pile-content {
  min-height: 55px;
  max-height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: visible;
}

/* Card Fan - centered fan layout with dynamic scaling */
.card-fan {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  transform: scale(var(--card-scale, 1));
  transform-origin: center bottom;
  height: 60px;
  min-width: 80px;
}

.fan-card {
  position: absolute;
  bottom: -15px;
  left: 50%;
  margin-left: -22px; /* Half of mini card width to center */
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.2s ease;
}

.fan-card.visible {
  opacity: 1;
}

/* Overflow Indicator */
.overflow-indicator {
  margin-left: 0.25rem;
  background: rgba(59, 130, 246, 0.8);
  color: white;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  align-self: center;
}

/* Empty Indicator */
.empty-indicator {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(16, 185, 129, 0.3);
  border-radius: 6px;
  color: #10b981;
}

.empty-indicator .material-symbols-outlined {
  font-size: 1.25rem;
  opacity: 0.5;
}

/* Pile Total */
.pile-total {
  font-size: 0.85rem;
  font-weight: 700;
  color: #10b981;
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-top: 15px;
}

.pile-total.visible {
  opacity: 1;
}

.pile-total.penalty {
  color: #f87171;
}

/* Total Row */
.total-row {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.9);
  transition: opacity 0.2s ease, transform 0.2s ease;
  margin-top: 0.25rem;
}

.total-row.visible {
  opacity: 1;
  transform: scale(1);
}

.total-penalty {
  background: rgba(220, 38, 38, 0.9);
  color: white;
  padding: 0.375rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 700;
}

.total-clean {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #10b981;
  font-weight: 600;
  font-size: 0.9rem;
}

.total-clean .material-symbols-outlined {
  font-size: 1.25rem;
}
</style>
